import {Component} from "@angular/core";
import {ActionStatusType} from "../../../common/enums/enums";
import {BehaviorSubject, combineLatest, Observable} from "rxjs";
import {distinctUntilChanged, map, shareReplay, switchMap} from "rxjs/operators";
import {ActivatedRoute, Params} from "@angular/router";
import {ActionsResource} from "../repository/actions-resource.service";
import {ActionsQuery} from "../repository/actions-query";
import {backdate, Period} from "../../../common/dates/date-manipulation";
import {today} from "../../../common/dates/today";
import {LoadsData, trackLoadingOn} from "../../../../angular/component/loading/loads-data";
import {Action} from "../../../common/model/action";
import {JobModalComponent, JobModalData} from "../../../buildings/issues/components/job-modal/job-modal.component";
import {MatDialog} from '@angular/material/dialog';

const BUILDING_ID_PARAM = 'buildingId';
const DEFAULT_STATUSES = [ActionStatusType.DECLINED, ActionStatusType.ACCEPTED, ActionStatusType.EXECUTING, ActionStatusType.FINISHED];

@Component({
  selector: 'app-building-actions-board',
  templateUrl: './building-actions-board.component.html',
  styleUrls: ['./building-actions-board.component.css']
})
export class BuildingActionsBoardComponent implements LoadsData {
  public statesToDisplay = [ActionStatusType.DECLINED, ActionStatusType.ACCEPTED, ActionStatusType.EXECUTING, ActionStatusType.FINISHED];
  public loading = false;
  private reloadData = new BehaviorSubject(null);

  constructor(
    private route: ActivatedRoute,
    private actions: ActionsResource,
    private dialog: MatDialog,
  ) {
  }

  public filterParameters$ = combineLatest(this.route.params, this.route.queryParams).pipe(
    distinctUntilChanged(),
    shareReplay(1),
  );

  public details$ = combineLatest(this.filterParameters$, this.reloadData.asObservable())
    .pipe(
    switchMap(([parameters]) => this.actionForBuilding$(parameters)),
    shareReplay(1)
  );

  public actionsWith(status: ActionStatusType): Observable<Action[]> {
    return this.details$.pipe(
      map(actions => actions.filter(action => action.status === status)),
      map(this.sortByDueDate),
      shareReplay(1)
    );
  }

  private actionForBuilding$([routeParams, queryparams]: [Params, Params]): Observable<Action[]> {
    const filters = this.buildFiltersFrom(routeParams, queryparams);
    const actionsRequest = this.actions.all(ActionsQuery.with(filters)).pipe(
        map(response => response.items)
    );
    return trackLoadingOn(actionsRequest, this, 'loading');
  }

  private sortByDueDate(actions: Action[]): Action[] {
    return actions.sort((lhs, rhs) => (
      lhs.dueDate && rhs.dueDate ?
      (new Date(lhs.dueDate)).getTime() - (new Date(rhs.dueDate)).getTime()
        : (lhs.dueDate ? -1 : 1)
    ));
  }

  private buildFiltersFrom(routeParams: Params, queryParams: Params): ActionsQuery {
    const buildingGuids = [routeParams[BUILDING_ID_PARAM]];
    const statuses = queryParams['statuses'] || DEFAULT_STATUSES;
    const selectionPeriod = parseInt(queryParams['selectionPeriodInMonths']);
    const interventionTeams = queryParams['interventionTeams'];
    const executedAfter = backdate(today(), selectionPeriod || 3, Period.Month);
    const type = queryParams['Type'];
    return ActionsQuery.with({buildingGuids, type, statuses, interventionTeams, executedAfter});
  }

  public openJobDialog(action: Action) {
    const data = {issueGuid: action.issues[0].issueGuid, action} as JobModalData;
    const dialog = this.dialog.open(JobModalComponent, {panelClass: 'full-bleed', data});
    dialog.componentInstance.actionAdded$.subscribe(() => {
     this.reloadData.next(null);
    });
  }
}
